import { isObject } from 'underscore';

import { RootState } from '@/store/main';
import { EventsTotals, GetEventsResponse } from '@/types/projects.types';
import { ThemePack } from '@/types/theme-packs.types';
import { GetProjects } from '@/connection/projects/get-projects';
import { GetThemePacks } from '@/connection/theme-packs';
import { PAGINATION_LIMIT } from '@/utils/pagination-constants';
import abortable from '@/connection/abortable';
import { FetchError } from '@/connection/helpers';
import toastTrigger from '@ui/toast/toast-trigger';
import { TemplatesReducerCreator } from '../types';
import { EToastTypes } from '@ui/toast/toast.types';

const fetchThemesAndTemplatesReducer = (create: TemplatesReducerCreator) => {
  return create.asyncThunk(
    async (
      {
        channel,
        returnStoreStateIfExists = true,
        offset,
        limit,
        search,
        includeTemplatesChannel,
        noV1Events,
        templateFilters,
        groupUuid,
        append,
        resetToPageNumber,
      }: {
        channel: number;
        returnStoreStateIfExists?: boolean;
        offset?: number;
        limit?: number;
        includeTemplatesChannel?: boolean;
        search?: string;
        noV1Events?: boolean;
        templateFilters?: { design?: string; categories?: string; favorites?: string; recent?: string };
        groupUuid?: string;
        append?: boolean;
        resetToPageNumber?: number;
      },
      { getState, rejectWithValue }
    ) => {
      try {
        const state = getState() as RootState;

        if (returnStoreStateIfExists) {
          // return from state if exists
          const themePacks = state?.templates?.themePacks as ThemePack[] | null;
          const templates = state?.templates?.templates as GetEventsResponse[] | null;
          const totalTemplates = state?.templates?.totalTemplates as EventsTotals | null;

          if (themePacks && templates) {
            return { templates, themePacks, totalTemplates, append, resetToPageNumber };
          }
        }

        abortable.abort('GetProjects', 'Refetching'); // abort any previous calls
        // abortable.abort('GetThemePacks'); // abort any previous calls

        const results: [Awaited<ReturnType<typeof GetProjects>>, Awaited<ReturnType<typeof GetThemePacks>>] =
          await Promise.all([
            GetProjects({
              channel,
              limit: limit || PAGINATION_LIMIT,
              offset: offset || 0,
              search,
              includeTemplatesChannel,
              noV1Events,
              templateFilters,
              selectedEventGroupUuid: groupUuid,
            }),
            GetThemePacks(channel),
          ]);
        const templates = results[0].events || [];
        const totalTemplates = results[0].totals || 0;
        const themePacks = results[1];
        return { templates, totalTemplates, themePacks, append, resetToPageNumber };
      } catch (e) {
        if (isObject(e) && (e.body === 'Navigation' || e.message === 'Fetch is aborted')) {
          // this just means we aborted, we will use this in the rejected handler to not throw a user message
          return rejectWithValue('Navigation');
        }

        if (e instanceof FetchError) {
          throw rejectWithValue(e.message);
        }

        throw rejectWithValue(String(e));
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.themePacks = action.payload.themePacks;
          if (action.payload.append) {
            state.templates = [...(state.templates || []), ...(action.payload.templates || [])];
          } else {
            state.templates = action.payload.templates;
            state.templatesListPage = 1;
          }
          state.totalTemplates = action.payload.totalTemplates;

          if (action.payload.resetToPageNumber !== undefined) {
            state.templatesListPage = action.payload.resetToPageNumber;
          }
          state.loadingTemplatesAndThemes = false;
        }
        state.renderLoadingList = 0;
      },
      pending: (state) => {
        state.loadingTemplatesAndThemes = true;
      },
      rejected: (state, action) => {
        if (action.payload instanceof Error) {
          console.error(action.payload);
        }

        // we aborted the previous request due to a new one
        // so we don't want to reset loading to false because it can race
        // with the incoming load set to true
        if (action.payload !== 'Refetching') {
          state.loadingTemplatesAndThemes = false;
          state.renderLoadingList = 0;
        }

        // don't throw a user message if we are simply abort the request
        if (action.payload === 'Navigation' || action.error?.message === 'Aborted') {
          return;
        }

        console.error(action.error);
        toastTrigger('There was an error loading templates and themes', {
          type: EToastTypes.error,
          autoClose: 10000,
          toastId: 'templates-loading-error',
          description: 'Please refresh the page',
        });
      },
    }
  );
};

export default fetchThemesAndTemplatesReducer;
